<template>
  <div>
    <mf-card-container
      v-model="search"
      title="Clientes"
      description="São apresentados aqui todos os clientes que se cadastraram via aplicativo ou site de cadastramento. Os cadastros feitos por outros canais, devem
              ser gerenciados por estes canais."
      use-search
      use-search-mode
      :search-mode-options="searchModeOptions"
      :selected-search-mode.sync="selectedSearchMode"
      :search-mode-style="{ width: '200px' }"
      placeholder="Busca"
    >
      <template v-slot:filters>
        <headers-data-table-settings
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          headers-settings-name="customer_management_register"
        />
      </template>
      <paginated-data-table
        ref="table"
        :headers.sync="computedHeaders"
        :search="search"
        :query="query"
        :query-variables="queryVariables"
        query-name="users"
        headers-settings-name="customers_management_register"
      >
        <template v-slot:[`item.document_no`]="{ item }">
          {{ item.document_no | cpfCnpj }}
        </template>
        <template v-slot:[`item.origin`]="{ item }">
          {{ formatString(item.origin) }}
        </template>
        <template v-slot:[`item.name`]="{ item }">
          {{ item.name }}
        </template>
        <template v-slot:[`item.phone`]="{ item }">
          {{ item.phone | telefone }}
        </template>
        <template v-slot:[`item.email`]="{ item }">
          {{ item.email | hifen }}
        </template>
        <template v-slot:[`item.last_accepted_terms_date`]="{ item }">
          {{ item.last_accepted_terms_date ? DateTime.fromISO(item.last_accepted_terms_date).toFormat('dd/MM/yyyy') : '-' }}
        </template>
        <template v-if="$getAllowedModules()['points']" v-slot:[`item.points.total`]="{ item }">
          {{ item.points.total || '-' }}
        </template>
        <template v-slot:[`item.created_at`]="{ item }">
          {{ DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy HH:mm:ss') | hifen }}
        </template>
        <template v-slot:[`item.gender`]="{ item }">
          {{ genders[item.gender] | hifen }}
        </template>
        <template v-slot:[`item.birth_date`]="{ item }">
          {{ DateTime.fromISO(item.birth_date).toFormat('dd/MM/yyyy') | hifen }}
        </template>
        <template v-slot:[`item.address.federative_unit`]="{ item }">
          {{ getSafe(() => item.address.federative_unit) | hifen }}
        </template>
        <template v-slot:[`item.address.city_name`]="{ item }">
          {{ getSafe(() => item.address.city_name) | hifen }}
        </template>
        <template v-slot:[`item.is_confirmed`]="{ item }">
          <mf-icon fill size="16" :color="item.is_confirmed ? 'verde-l10' : 'cinza-0'" icon="fiber_manual_record" />
          {{ item.is_confirmed ? 'Ativo' : 'Inativo' }}
        </template>
        <template v-slot:[`item.confirmed_at`]="{ item }">
          {{ item.confirmed_at ? DateTime.fromISO(item.confirmed_at).toFormat('dd/MM/yyyy') : '-' }}
        </template>
        <template v-if="showEditRegister || showAdjustPoints" v-slot:[`item.expand`]="{ item }">
          <v-menu bottom left :close-on-content-click="false" class="text-right">
            <template v-slot:activator="{ on }">
              <mf-button type="icon" icon="more_vert" color="cinza-0" v-on:click.native="on.click" />
            </template>
            <v-list>
              <v-list-item v-if="showEditRegister" @click="editUser(item)">
                <v-list-item-title>Editar cadastro</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="showEditRegister" @click="changePassword(item)">
                <v-list-item-title>Trocar senha</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="showAdjustPoints" @click="adjustPoints(item)">
                <v-list-item-title>Ajustar pontos</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="showAdjustPoints" @click="showPointsHistory(item)">
                <v-list-item-title>Ver histórico de pontos</v-list-item-title>
              </v-list-item>
              <v-list-group v-if="isConsumerActivationAfterSignUp && !item.is_confirmed">
                <template v-slot:activator>
                  <v-list-item-content>
                    <v-list-item-title>Reenviar link de ativação</v-list-item-title>
                  </v-list-item-content>
                </template>
                <v-list-item link>
                  <v-list-item-title @click="resendActivationLink(item, 'SMS')">Reenviar por SMS</v-list-item-title>
                </v-list-item>
                <v-list-item link>
                  <v-list-item-title @click="resendActivationLink(item, 'EMAIL')">Reenviar por e-mail</v-list-item-title>
                </v-list-item>
              </v-list-group>
              <v-list-item v-if="isConsumerActivationAfterSignUp && !item.is_confirmed" @click="activateManually(item)">
                <v-list-item-title>Ativar manualmente</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </paginated-data-table>
    </mf-card-container>
    <adjust-points v-if="adjustingPoints" v-model="adjustingPoints" :user="selectedUser" @save="$refs.table.waitAndGetData()" />
    <points-history v-if="showingPointsHistory" v-model="showingPointsHistory" :user="selectedUser" @save="$refs.table.waitAndGetData()" />
    <change-password v-if="changingPassword" v-model="changingPassword" :user="selectedUser" />
    <edit-user
      v-if="editingUser"
      v-model="editingUser"
      :user="selectedUser"
      :account-configuration="accountConfiguration"
      @save="$refs.table.waitAndGetData()"
    />
  </div>
</template>

<script>
import { QUERY_GET_USERS, ACCOUNT_OR_FRANCHISE_HOTSITE_SIGNUP_CONFIGURATION_INPUT } from '@/modules/clientes/gerenciamento/graphql'
import { DateTime } from 'luxon'
import cloneDeep from 'lodash/cloneDeep'
import userActivation from '@/modules/clientes/gerenciamento/mixins/userActivation'

export default {
  name: 'GerenciamentoClientesCadastro',
  components: {
    HeadersDataTableSettings: () => import('@/components/HeadersDataTableSettings.vue'),
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
    AdjustPoints: () => import('@/modules/clientes/gerenciamento/views/components/AdjustPoints.vue'),
    PointsHistory: () => import('@/modules/clientes/gerenciamento/views/components/PointsHistory.vue'),
    ChangePassword: () => import('@/modules/clientes/gerenciamento/views/components/ChangePassword.vue'),
    EditUser: () => import('@/modules/clientes/gerenciamento/views/components/EditUser.vue'),
  },
  mixins: [userActivation],
  data: () => ({
    search: '',
    selectedSearchMode: 'documentNo',
    selectedUser: {},
    accountConfiguration: {},
    adjustingPoints: false,
    showingPointsHistory: false,
    changingPassword: false,
    editingUser: false,
    DateTime: DateTime,
    headers: [],
  }),

  computed: {
    queryVariables() {
      return {
        searchMode: this.selectedSearchMode,
      }
    },
    searchModeOptions() {
      return [
        { text: 'CPF/CNPJ', value: 'documentNo' },
        { text: 'Nome', value: 'name' },
        { text: 'Telefone', value: 'phone' },
        { text: 'E-mail', value: 'email' },
        { text: 'Todos', value: 'all' },
      ]
    },
    isConsumerActivationAfterSignUp() {
      return this.$getAllowedModules()['consumer_activation_after_sign_up']
    },
    showEditRegister() {
      return this.$permissions.havePagePermission('clientes/gerenciamento/cadastros/edicao-cadastros')
    },
    showAdjustPoints() {
      return this.$permissions.havePagePermission('clientes/gerenciamento/cadastros/edicao-pontos') && this.$getAllowedModules()['points']
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    query() {
      return QUERY_GET_USERS
    },
    genders() {
      return { M: 'Masculino', F: 'Feminino', U: 'Não informado' }
    },
    defaultHeaders() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'document_no',
          isActive: true,
          isRequired: true,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'Origem',
          value: 'origin',
          isActive: true,
          isRequired: false,
          isDefault: true,
          width: '5%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'name',
          isActive: true,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'Telefone',
          value: 'phone',
          isActive: false,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'E-mail',
          value: 'email',
          isActive: true,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        ...(this.$getAllowedModules()['points']
          ? [
              {
                text: 'Pontos',
                value: 'points.total',
                isActive: true,
                isRequired: false,
                isDefault: true,
                width: '10%',
                sortable: true,
              },
            ]
          : []),
        {
          text: 'Data do último aceite',
          value: 'last_accepted_terms_date',
          isActive: true,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'Data de cadastro',
          value: 'created_at',
          isActive: false,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'Gênero',
          value: 'gender',
          isActive: false,
          isRequired: false,
          isDefault: true,
          sortable: true,
        },
        {
          text: 'Data de nascimento',
          value: 'birth_date',
          isActive: false,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        {
          text: 'Estado',
          value: 'address.federative_unit',
          isActive: false,
          isRequired: false,
          isDefault: true,
          sortable: true,
        },
        {
          text: 'Cidade',
          value: 'address.city_name',
          isActive: false,
          isRequired: false,
          isDefault: true,
          width: '10%',
          sortable: true,
        },
        ...(this.isConsumerActivationAfterSignUp
          ? [
              {
                text: 'Status',
                value: 'is_confirmed',
                isActive: true,
                isRequired: false,
                isDefault: true,
                width: '5%',
                sortable: false,
              },
              {
                text: 'Data de ativação',
                value: 'confirmed_at',
                isActive: true,
                isRequired: false,
                isDefault: true,
                width: '10%',
                sortable: false,
              },
            ]
          : []),
        {
          text: '',
          value: 'expand',
          sortable: false,
          align: 'right',
          width: '5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
      ]
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          width: header.width,
          sortable: header.hasOwnProperty('sortable') ? header.sortable : true,
        }
      })
    },
  },
  mounted() {
    this.getAccountConfiguration()
  },
  methods: {
    async getAccountConfiguration() {
      const { data } = await this.$apollo.query({
        query: ACCOUNT_OR_FRANCHISE_HOTSITE_SIGNUP_CONFIGURATION_INPUT,
        fetchPolicy: 'no-cache',
      })
      this.accountConfiguration = data?.hotsiteConfiguration?.configuration
    },
    reloadDataAfterActivation() {
      this.$refs.table.waitAndGetData()
    },
    formatString(string) {
      return string ? string[0].toUpperCase().concat(string.substring(1)) : '-'
    },
    editUser(user) {
      this.selectedUser = user
      this.editingUser = true
    },
    changePassword(user) {
      this.selectedUser = user
      this.changingPassword = true
    },
    adjustPoints(user) {
      this.selectedUser = user
      this.adjustingPoints = true
    },
    showPointsHistory(user) {
      this.selectedUser = user
      this.showingPointsHistory = true
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
  },
}
</script>
<style lang="scss" scoped>
.section-subtitle {
  @extend .texto-corrido;
  word-break: break-word;
}
</style>
